import { CheckboxTickIcon, CustomersTabIcon, NavCloseIcon, ThinSearchIcon } from 'assets/icons';
import { CustomCheckbox, CustomInput } from 'components';
import CustomAvatar from 'components/avatar/CustomAvatar';
import {
  selectCurrentTeam,
  selectCurrentTeamJobPermissions,
  selectCurrentTeamTeamSettingsPermissions,
  selectUserDetails,
  showNotifier
} from 'containers/app/appSlice';
import { useLazyGetAssignableMembersQuery } from 'containers/jobs/api';
import { AssignableMember } from 'containers/jobs/jobs.model';
import { useAppDispatch, useAppSelector } from 'hooks';
import { FC, useContext, useEffect, useState } from 'react';
import { getInitials } from 'utils/commonUtils';
import AssignableMembersModal from './AssignableMembersModal';
import OutsideClickHandle from 'components/outside-click-handler/OutsideClickHandler';
import {
  removeJobValidationErrors,
  selectAssigneeList,
  selectJobValidationErrors,
  updateAssigneeList
} from '../createJobSlice';
import { CreateEditJobNavEnum, CreateJobErrorTypes } from 'containers/jobs/constant';
import { getJobValidationErrors } from 'containers/jobs/utils';
import { CreateJobContextType } from 'containers/jobs/types';
import { NotifierTypes } from 'constants/notifierConstants';

interface AssignMembersSectionProps {
  className?: string;
  context: React.Context<CreateJobContextType>;
}

const AssignMembersSection: FC<AssignMembersSectionProps> = ({ context }) => {
  const { selectedJobTab, setSelectedJobTab } = useContext<CreateJobContextType>(context);
  const [searchText, setSearchText] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestionList, setSuggestionList] = useState<AssignableMember[]>([]);
  const [openAssignMembersModal, setOpenAssignMembersModal] = useState(false);

  const currentTeam = useAppSelector(selectCurrentTeam);
  const currentUser = useAppSelector(selectUserDetails);
  const selectedJobValidations = useAppSelector(selectJobValidationErrors);
  const assigneeList = useAppSelector(selectAssigneeList);
  const manageTeamPermissions = useAppSelector(selectCurrentTeamTeamSettingsPermissions);
  const { manageOthersJobs } = useAppSelector(selectCurrentTeamJobPermissions);

  const dispatch = useAppDispatch();

  const [getAssignableTeamMemberQuery, { data: assignableTeamMembers, isSuccess: assignableTeamMembersSuccess }] =
    useLazyGetAssignableMembersQuery();
  const isSingleMemberTeam = assignableTeamMembers?.data?.teamMemberList?.length === 1;

  useEffect(() => {
    if (currentTeam?.id) {
      getAssignableTeamMemberQuery(currentTeam?.id.toString());
    }
  }, [currentTeam?.id]);

  useEffect(() => {
    if (assignableTeamMembersSuccess) {
      let assignableMembersList = assignableTeamMembers.data.teamMemberList || [];
      if (!manageOthersJobs) {
        assignableMembersList = assignableTeamMembers.data.teamMemberList?.filter(
          member => member.merchantId === currentUser?.id
        );
      }
      setSuggestionList(assignableMembersList);
      if (isSingleMemberTeam) {
        const selfAssigned = assignableMembersList.find(member => member.merchantId === currentUser.id);
        selfAssigned && addToAssigneeList(selfAssigned);
      }
    }
  }, [assignableTeamMembersSuccess]);

  const handleAssigneeListUpdation = list => {
    dispatch(updateAssigneeList(list));
    dispatch(removeJobValidationErrors(CreateJobErrorTypes.ASSIGN_MEMBER));
  };

  const removeFromAssigneeList = (memberId: string) => {
    if (!manageOthersJobs) {
      const isNotSelf = memberId !== currentUser?.id;
      if (isNotSelf) {
        dispatch(
          showNotifier({
            message: {
              primaryMessage: 'You do not have permission to remove this member'
            },
            type: NotifierTypes.INFO,
            showClose: false,
            fontStyle: 'text-primary font-normal'
          })
        );
        return;
      }
    }
    const updatedAssigneeList = assigneeList.filter(member => member.merchantId !== memberId);
    handleAssigneeListUpdation(updatedAssigneeList);
  };

  const addToAssigneeList = member => {
    const currentList = [...assigneeList];
    const isAlreadyAdded = currentList.findIndex(item => item.merchantId === member.merchantId);
    if (isAlreadyAdded > -1) {
      return;
    }
    const isSelfAssigned = member.merchantId === currentUser.id;
    if (isSelfAssigned) {
      currentList.unshift(member);
    } else {
      currentList.push(member);
    }
    handleAssigneeListUpdation(currentList);
  };

  const toggleJobSelfAssigned = () => {
    if (isSingleMemberTeam) return;
    if (checkIfJobIsSelfAssigned()) {
      removeFromAssigneeList(currentUser.id);
    } else {
      const selfAssigned = suggestionList.find(member => member.merchantId === currentUser.id);
      addToAssigneeList(selfAssigned);
    }
    if (selectedJobTab !== CreateEditJobNavEnum.JOB_SCHEDULE) {
      setSelectedJobTab(CreateEditJobNavEnum.JOB_SCHEDULE);
    }
  };

  const handleSearchChange = value => {
    setSearchText(value);
    if (value === '') {
      setShowSuggestions(false);
    } else {
      setShowSuggestions(true);
    }
  };

  const handleOpenAssignMembersModal = () => {
    setOpenAssignMembersModal(true);
  };

  const handleCloseAssignMembersModal = () => {
    setOpenAssignMembersModal(false);
  };

  const handleSave = membersList => {
    handleCloseAssignMembersModal();
    handleAssigneeListUpdation(membersList);
  };

  const assigneeError = getJobValidationErrors(selectedJobValidations, CreateJobErrorTypes.ASSIGN_MEMBER);

  const checkIfJobIsSelfAssigned = () => {
    if (isSingleMemberTeam) return true;
    return assigneeList?.some(member => member.merchantId === currentUser.id);
  };

  const renderSuggestions = () => {
    const list = suggestionList?.filter(member => member.name.toLowerCase().includes(searchText.toLowerCase()));
    return list?.length > 0 ? (
      list?.map((member, index) => (
        <div
          onClick={e => {
            e.preventDefault();
            addToAssigneeList(member);
            handleSearchChange('');
          }}
          className="flex cursor-pointer gap-5 rounded border-b 
        border-borderGray py-3 px-1 last:border-b-0 hover:bg-highlightGray"
          key={member.name + index}>
          <div>
            <CustomAvatar src={member.profilePictureUrl}>{getInitials(member.name || member.phone)}</CustomAvatar>
          </div>
          <div className="text-lg font-semibold leading-5 text-headingGray">{`${member.name}`}</div>
        </div>
      ))
    ) : (
      <div className="mx-auto my-5 text-sbase font-semibold text-primaryText">No results found!</div>
    );
  };

  const renderAssigneeList = () => {
    return assigneeList?.map((member, index) => (
      <div key={member.name + index} className="flex items-center gap-2 rounded-[10px] bg-borderGray py-1 px-2">
        <div className="text-sbase text-primaryText">
          {member.name}
          {member.merchantId === currentUser.id && <span> {'(you)'}</span>}
        </div>
        <NavCloseIcon
          className="path-fill-current path cursor-pointer text-gray-1"
          width={10}
          height={10}
          onClick={() => removeFromAssigneeList(member.merchantId)}
        />
      </div>
    ));
  };

  return (
    <div className="w-full">
      <div className="pb-2 text-17px font-semibold text-heading">Assign team members</div>
      {assigneeError && <div className="text-sbase font-semibold text-error">{assigneeError}</div>}
      <div className="flex items-center gap-2.5 pt-2">
        <CustomCheckbox
          id="assign-job-to-self"
          onClick={toggleJobSelfAssigned}
          isChecked={checkIfJobIsSelfAssigned()}
          className={isSingleMemberTeam ? 'cursor-default' : ''}>
          {checkIfJobIsSelfAssigned() && <CheckboxTickIcon />}
        </CustomCheckbox>
        <label htmlFor="assign-job-to-self" className="text-sbase text-secondary">
          Assign to myself
        </label>
      </div>

      {(manageTeamPermissions.viewExistingMember || manageTeamPermissions.editExistingMember) &&
        !isSingleMemberTeam && (
          <div className="mt-5 flex items-start justify-between rounded bg-secondaryBtn px-5 py-2.5">
            <div className="relative flex grow items-start ">
              <div className="flex h-[26px] items-center justify-center">
                <ThinSearchIcon />
              </div>

              <div className="flex flex-wrap gap-2.5 px-3">
                {renderAssigneeList()}
                <CustomInput
                  id="assign-team-members-search"
                  value={searchText}
                  onChange={handleSearchChange}
                  placeholder={assigneeList?.length > 0 ? 'Search another member' : 'Search a member'}
                  className="h-6 p-0 outline-none"
                />
              </div>
              {showSuggestions && (
                <OutsideClickHandle
                  onOutsideClick={() => {
                    handleSearchChange('');
                  }}>
                  <div
                    className="absolute top-full -left-5 z-10 w-full 
                    rounded border border-borderGray bg-primary px-5 py-2 shadow-md">
                    {renderSuggestions()}
                  </div>
                </OutsideClickHandle>
              )}
            </div>

            <div
              className="flex shrink-0 cursor-pointer items-center gap-2"
              role="presentation"
              onClick={handleOpenAssignMembersModal}>
              <CustomersTabIcon
                className="path-stroke-current path-fill-current path text-secondary"
                width={24}
                height={24}
              />
              <div className="text-17px font-semibold text-secondary">View all members</div>
            </div>
          </div>
        )}
      {openAssignMembersModal && (
        <AssignableMembersModal
          list={suggestionList}
          onClose={handleCloseAssignMembersModal}
          selectedMembers={assigneeList}
          onSave={handleSave}
        />
      )}
    </div>
  );
};

export default AssignMembersSection;
